import React, { useContext, useState } from 'react';
import { Button, FormControl, FormLabel, Stack } from '@mui/material';
import parse from 'html-react-parser';
import { useQueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';

import Modal from 'components/_layouts/Modal';
import ManualPicker from 'components/_pickers/ManualPicker';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import useIncompleteAcksReportExport from '../../_data_sources/useIncompleteAcksReportExport';

type IncompleteAcksModalProps = {
  open: boolean;
  onClose: () => void;
};

const IncompleteAcksModal: React.FC<IncompleteAcksModalProps> = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useContext(AppContext);
  const { generate: generateReport } = useIncompleteAcksReportExport();

  const [selectedManual, setSelectedManual] = useState<string[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (!selectedManual.length || !currentOrganizationId) return;

    setIsDownloading(true);

    try {
      await generateReport(
        currentOrganizationId,
        { manualId: selectedManual[0] },
        () => {
          enqueueSnackbar(parse(t('reporting.policyAcknowledgments.incompleteAcks.retrievalConfirmation', '', {})), {
            variant: 'success',
            autoHideDuration: 6000,
          });
          window.setTimeout(() => {
            queryClient.invalidateQueries('notifications_unread_count');
            queryClient.invalidateQueries('notifications');
          }, 15000);
          setIsDownloading(false);
          handleClose();
        },
        () => {
          setIsDownloading(false);
        }
      );
    } catch {
      setIsDownloading(false);
    }
  };

  const handleClose = () => {
    setSelectedManual([]);
    setIsDownloading(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      subtitle={t('reporting.policyAcknowledgments.incompleteAcks.modal.subtitle')}
      title={t('reporting.policyAcknowledgments.incompleteAcks.modal.title')}
      actions={
        <Stack
          direction='row'
          spacing={3}
        >
          <Button
            variant='outlined'
            onClick={handleClose}
            disabled={isDownloading}
          >
            {t('reporting.policyAcknowledgments.incompleteAcks.modal.cancel')}
          </Button>
          <Button
            variant='contained'
            onClick={handleDownload}
            disabled={isDownloading || !selectedManual.length}
            loading={isDownloading}
            loadingPosition='end'
          >
            {isDownloading
              ? t('reporting.policyAcknowledgments.incompleteAcks.modal.downloading')
              : t('reporting.policyAcknowledgments.incompleteAcks.modal.downloadReport')}
          </Button>
        </Stack>
      }
    >
      <FormControl fullWidth>
        <FormLabel>{t('reporting.policyAcknowledgments.incompleteAcks.modal.manualLabel')}</FormLabel>
        <ManualPicker
          id='incomplete-acks-manual-picker'
          organizationId={currentOrganizationId || ''}
          value={selectedManual}
          onChange={setSelectedManual}
          placeholder={t('reporting.policyAcknowledgments.incompleteAcks.modal.manualPlaceholder')}
        />
      </FormControl>
    </Modal>
  );
};

export default IncompleteAcksModal;
